import type { ContractDocument } from "../types/contractDocument";
import { formatDisplayDate } from "../utils/displayDate";
import { mockContractDocuments } from "../data/contractDocuments";
import { BASE_URL, apiRequest } from "./apiClient";

export { BASE_URL };

type BackendContractListItem = {
  readonly id: string;
  readonly name?: string | null;
  readonly contract_type?: string | null;
  readonly status?: string | null;
  readonly stage?: string | null;
  readonly version_number?: number | null;
  readonly created_at?: string | null;
  readonly updated_at?: string | null;
  readonly created_by_name?: string | null;
  readonly created_by_email?: string | null;
  readonly original_download_url?: string | null;
};

/** PaginatedResponse from GET /contracts: `data` is required; `meta` has total/limit/offset. */
type BackendContractsResponse = {
  readonly data: BackendContractListItem[];
  readonly meta?: {
    readonly total?: number;
    readonly limit?: number;
    readonly offset?: number;
  };
};

export type ContractsSortField =
  | "created_at"
  | "updated_at"
  | "name"
  | "status"
  | "stage"
  | "contract_type"
  | "created_by";

export type FetchContractsParams = {
  readonly q?: string;
  readonly contract_type?: string;
  readonly sort_by?: ContractsSortField;
  readonly sort_dir?: "asc" | "desc";
  readonly limit?: number;
  readonly offset?: number;
};

export type FetchContractsResult = {
  readonly items: ContractDocument[];
  readonly total: number;
};

const toContractDocument = (item: BackendContractListItem): ContractDocument => {
  const name = item.name && item.name.trim().length > 0 ? item.name : "Untitled Contract";
  const createdDate = formatDisplayDate(
    item.updated_at ?? item.created_at ?? new Date().toISOString()
  );

  return {
    id: item.id,
    documentName: name,
    contractType: item.contract_type ?? "Contract",
    status: item.status?.trim() || "Unknown",
    stage: item.stage?.trim() || "Unknown",
    createdBy: item.created_by_name ?? "Unknown",
    createdByEmail: item.created_by_email ?? "unknown@example.com",
    createdDate,
    originalDownloadUrl: item.original_download_url ?? null,
  };
};

function buildContractsQuery(params?: FetchContractsParams): string {
  if (!params) {
    return "/contracts";
  }
  const search = new URLSearchParams();
  if (params.q?.trim()) {
    search.set("q", params.q.trim());
  }
  if (params.contract_type?.trim()) {
    search.set("contract_type", params.contract_type.trim());
  }
  if (params.sort_by) {
    search.set("sort_by", params.sort_by);
  }
  if (params.sort_dir) {
    search.set("sort_dir", params.sort_dir);
  }
  if (params.limit != null) {
    search.set("limit", String(params.limit));
  }
  if (params.offset != null) {
    search.set("offset", String(params.offset));
  }
  const qs = search.toString();
  return qs.length > 0 ? `/contracts?${qs}` : "/contracts";
}

export const fetchContracts = async (
  params?: FetchContractsParams
): Promise<FetchContractsResult> => {
  try {
    const path = buildContractsQuery(params);
    const response = await apiRequest(path);

    const json = (await response.json()) as BackendContractsResponse;
    if (!Array.isArray(json.data)) {
      return { items: [], total: 0 };
    }

    const items = json.data.map(toContractDocument);
    const total = json.meta?.total ?? items.length;
    return { items, total };
  } catch {
    const items = [...mockContractDocuments];
    return { items, total: items.length };
  }
};
